package diana.soleil.design.iraniancanadienbazzar

import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.window.ComposeViewport
import diana.soleil.design.iraniancanadienbazzar.core.common.AppBuildInfo
import diana.soleil.design.iraniancanadienbazzar.core.model.GeoPoint
import diana.soleil.design.iraniancanadienbazzar.core.model.PickedImage
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.FirebaseOptions
import dev.gitlive.firebase.firestore.firestore
import dev.gitlive.firebase.firestore.firestoreSettings
import dev.gitlive.firebase.firestore.persistentCacheSettings
import dev.gitlive.firebase.initialize
import kotlin.coroutines.resume
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.channels.awaitClose
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.callbackFlow
import kotlinx.coroutines.suspendCancellableCoroutine
import org.w3c.dom.events.Event
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Int8Array
import org.w3c.dom.HTMLInputElement
import org.w3c.files.FileReader

/**
 * The two Firebase web targets. `dev` is the default; `prod` is opted into for a pre-release preview
 * (see [selectedEnv]). Both apiKeys are public — access is gated by Firestore security rules.
 */
private class WebFirebaseEnv(
    val name: String,
    val options: FirebaseOptions,
)

private val DEV_ENV = WebFirebaseEnv(
    name = "dev",
    options = FirebaseOptions(
        applicationId = "1:180233466577:web:fd27ac2d38ecca2d71cbf0",
        apiKey = "AIzaSyCZtO6rDc9jQfbC375YVoAI7R6YnmgYw3k",
        projectId = "iranian-business-hub-dev",
        storageBucket = "iranian-business-hub-dev.firebasestorage.app",
        gcmSenderId = "180233466577",
        authDomain = "iranian-business-hub-dev.firebaseapp.com",
    ),
)

// Real prod web config (project iranian-business-hub-can-67edd). The web apiKey is NOT a secret —
// it ships in the bundle; access is gated by Firestore/Storage rules. Mirrors firebase/web-app-config.js.
private val PROD_ENV = WebFirebaseEnv(
    name = "prod",
    options = FirebaseOptions(
        applicationId = "1:589166323510:web:3bdb7ab2d69ff851753597",
        apiKey = "AIzaSyDRorxs7L4xjN79C0irCUfVfcmKT2yEs70",
        projectId = "iranian-business-hub-can-67edd",
        storageBucket = "iranian-business-hub-can-67edd.firebasestorage.app",
        gcmSenderId = "589166323510",
        authDomain = "iranian-business-hub-can-67edd.firebaseapp.com",
    ),
)

private class WebRuntime(val env: WebFirebaseEnv, val isDebug: Boolean)

/**
 * Resolves which Firebase project the web app talks to AND whether it runs as debug (dev chrome) or
 * production. There is a single web build, so this is decided at runtime:
 *  - Env: an explicit override (`?env=prod|dev` or `localStorage.mahalehEnv`) always wins; otherwise the
 *    HOST decides — the prod hosting site (…-can-67edd.web.app / mahaleh.ca) → PROD, dev + localhost → DEV.
 *  - isDebug: FALSE only for the untouched prod site (prod host, no override) — real users get NO dev
 *    menu and NO "LIVE PROD" banner. Everything else (localhost, dev host, or a prod-preview via
 *    `?env=prod`) is debug, so the prod-preview still shows the banner + dev tools.
 */
private fun webRuntime(): WebRuntime {
    val queryEnv = js("new URLSearchParams(window.location.search).get('env')") as String?
    val storageEnv = js("window.localStorage.getItem('mahalehEnv')") as String?
    val override = queryEnv ?: storageEnv
    val host = js("window.location.hostname") as String
    val isProdHost = host == "iranian-business-hub-can-67edd.web.app" || host.endsWith("mahaleh.ca")
    val env = when {
        override == "prod" -> PROD_ENV
        override == "dev" -> DEV_ENV
        isProdHost -> PROD_ENV
        else -> DEV_ENV
    }
    val isProduction = isProdHost && override == null
    return WebRuntime(env = env, isDebug = !isProduction)
}

/**
 * Web entry point. Initializes the Firebase JS app before composing, then runs the app against
 * **live Cloud Firestore** (`useFirestore = true`) — as do the Android and iOS entry points; the
 * in-memory fakes remain only for tests and the Developer Menu's mock-data mode.
 */
@OptIn(ExperimentalComposeUiApi::class)
fun main() {
    val runtime = webRuntime()
    Firebase.initialize(context = null, options = runtime.env.options)
    // Stash the web config so the firebase/auth modular import (used for Google redirect) can
    // getApp()-or-initializeApp() on the SAME default app — its module instance otherwise reports
    // "No Firebase App '[DEFAULT]'".
    val opts = runtime.env.options
    val fbCfg: dynamic = js("({})")
    fbCfg.apiKey = opts.apiKey
    fbCfg.authDomain = opts.authDomain
    fbCfg.projectId = opts.projectId
    fbCfg.appId = opts.applicationId
    fbCfg.storageBucket = opts.storageBucket
    fbCfg.messagingSenderId = opts.gcmSenderId
    val win: dynamic = js("window")
    win.__mahalehFb = fbCfg
    // Firestore JS defaults to a MEMORY-ONLY cache (unlike Android/iOS), so a flaky connection
    // errors every screen instead of serving cached data. Opt into IndexedDB persistence before
    // any Firestore use.
    Firebase.firestore.settings = firestoreSettings {
        cacheSettings = persistentCacheSettings { }
    }
    // Finish a Google sign-in that redirected away and came back (no-op otherwise).
    completeGoogleRedirect()
    // Boot deep-linking is handled by BindWebNavHistory (AppNavHost): it strips the /app mount
    // prefix before parsing, which this raw-href call never did (…/app/b/x parsed to nothing on
    // prod, and on the dev server it DOUBLE-offered with the history binding — QA round-7).
    ComposeViewport {
        App(
            useFirestore = true,
            buildInfo = AppBuildInfo(
                versionName = "web",
                versionCode = "0",
                firebaseEnv = runtime.env.name,
                isDebug = runtime.isDebug,
            ),
            shareText = { text ->
                // navigator.share where available (mobile browsers); clipboard elsewhere.
                js("navigator.share ? navigator.share({text: text}) : navigator.clipboard.writeText(text)")
            },
            getUserLocation = { browserLocation() },
            pickImage = { pickImageInBrowser() },
            googleIdToken = { webGoogleIdToken() },
            connectivity = browserConnectivity(),
            // Share-sheet network targets — new tab so the share page never replaces the app.
            openUrl = { url -> window.open(url, "_blank") },
        )
    }
}

/**
 * Connectivity for the shell's offline banner: `navigator.onLine` now, then the browser's
 * `online`/`offline` events. (onLine=true can be a lie on captive portals, but false is reliable —
 * exactly what the banner needs.)
 */
private fun browserConnectivity(): Flow<Boolean> = callbackFlow {
    trySend(window.navigator.onLine)
    val onOnline: (Event) -> Unit = { trySend(true) }
    val onOffline: (Event) -> Unit = { trySend(false) }
    window.addEventListener("online", onOnline)
    window.addEventListener("offline", onOffline)
    awaitClose {
        window.removeEventListener("online", onOnline)
        window.removeEventListener("offline", onOffline)
    }
}

/**
 * Starts Firebase "Sign in with Google" on web via a full-page **redirect** (not a popup — popups
 * opened after the app's async click handler are blocked by browsers regardless of settings). The page
 * navigates to Google, then back; [completeGoogleRedirect] finishes the sign-in on return, which flips
 * the auth state and navigates onward. Guests use `linkWithRedirect` so their data survives the upgrade.
 * Returns null: nothing to return since the page unloads.
 */
private suspend fun webGoogleIdToken(): String? = suspendCancellableCoroutine { cont ->
    val promise: dynamic = js(
        """(function() {
          console.log('[mahaleh] google sign-in: starting');
          return Promise.all([import('firebase/app'), import('firebase/auth')]).then(function(mods) {
            var appMod = mods[0], m = mods[1];
            var app; try { app = appMod.getApp(); } catch (e) { app = appMod.initializeApp(window.__mahalehFb); }
            var auth = m.getAuth(app);
            var provider = new m.GoogleAuthProvider();
            var cur = auth.currentUser;
            if (cur && cur.isAnonymous) {
              console.log('[mahaleh] google: linkWithRedirect (guest upgrade)');
              return m.linkWithRedirect(cur, provider);
            }
            console.log('[mahaleh] google: signInWithRedirect');
            return m.signInWithRedirect(auth, provider);
          }).catch(function(e) {
            console.error('[mahaleh] google sign-in error:', (e && e.code) || e, e);
          });
        })()""",
    )
    promise.then(
        { _: dynamic -> cont.resume(null) },
        { _: dynamic -> cont.resume(null) },
    )
}

/**
 * On page load, completes a pending Google redirect sign-in (no-op if there is none). Firebase updates
 * the default auth's state, which GitLive's auth-state listener (the session in AuthViewModel) observes.
 */
private fun completeGoogleRedirect() {
    js(
        """Promise.all([import('firebase/app'), import('firebase/auth')]).then(function(mods) {
          var appMod = mods[0], m = mods[1];
          var app; try { app = appMod.getApp(); } catch (e) { app = appMod.initializeApp(window.__mahalehFb); }
          return m.getRedirectResult(m.getAuth(app));
        }).then(function(result) {
          if (result && result.user) { console.log('[mahaleh] google redirect completed:', result.user.uid); }
        }).catch(function(e) {
          console.error('[mahaleh] getRedirectResult error:', (e && e.code) || e, e);
        });"""
    )
}

/** Opens a hidden file input, reads the chosen image as bytes, or null if the user cancels/errors. */
private suspend fun pickImageInBrowser(): PickedImage? = suspendCancellableCoroutine { cont ->
    val input = document.createElement("input") as HTMLInputElement
    input.type = "file"
    input.accept = "image/*"
    // Browsers fire `change` only when a file is chosen — dismissing the dialog fires `cancel`
    // (modern Chromium/Firefox/Safari) or nothing at all. Without resuming on cancel, every
    // dismissed dialog left this coroutine suspended forever in viewModelScope.
    var settled = false
    fun settle(result: PickedImage?) {
        if (!settled && cont.isActive) {
            settled = true
            cont.resume(result)
        }
    }
    input.onchange = {
        val file = input.files?.item(0)
        if (file == null) {
            settle(null)
        } else {
            val reader = FileReader()
            reader.onload = {
                // Kotlin/JS ByteArray is backed by an Int8Array, so this view is the bytes directly.
                val bytes = Int8Array(reader.result as ArrayBuffer).unsafeCast<ByteArray>()
                settle(PickedImage(bytes, file.type.ifEmpty { "image/jpeg" }))
                null
            }
            reader.onerror = { settle(null); null }
            reader.readAsArrayBuffer(file)
        }
        null
    }
    input.addEventListener("cancel", { settle(null) })
    input.click()
}

/** The browser's current position via navigator.geolocation, or null if unsupported/denied/errored. */
private suspend fun browserLocation(): GeoPoint? = suspendCancellableCoroutine { cont ->
    val geo: dynamic = js("(typeof navigator !== 'undefined' && navigator.geolocation) ? navigator.geolocation : null")
    if (geo == null) {
        cont.resume(null)
    } else {
        geo.getCurrentPosition(
            { position: dynamic -> cont.resume(GeoPoint(position.coords.latitude as Double, position.coords.longitude as Double)) },
            { _: dynamic -> cont.resume(null) },
        )
    }
}
